import { Component, ElementRef, HostListener, OnChanges, OnDestroy, OnInit, Renderer2, SimpleChanges, ViewChild, ChangeDetectorRef } from '@angular/core';
import { Order } from '../Module/Order.model';
import { Product } from '../Module/Product.model';
import { OrderLine } from '../Module/OrderLine.model';
import { ProductService } from '../service/product.service';
import { map, Observable, of, Subject } from 'rxjs';
import { OrderService } from '../service/order.service';
import { HttpResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { IsNoPaidOrderPopUpComponent } from '../is-no-paid-order-pop-up/is-no-paid-order-pop-up.component';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, finalize, takeUntil } from 'rxjs/operators';

import { Client } from '../Module/Client.model';
import { StateService } from '../service/state.service';
import { DeleteConfirmationDialogComponent } from '../delete-confirmation-dialog/delete-confirmation-dialog.component';
import { InsufficientQuantityDialogComponent } from '../insufficient-quantity-dialog/insufficient-quantity-dialog.component';
import { GenericErrorDialogComponent } from '../generic-error-dialog/generic-error-dialog.component';
import { InvalidSellingPriceDialogComponent } from '../invalid-selling-price-dialog/invalid-selling-price-dialog.component';
import { ClientService } from '../service/client.service';
import { LoaderService } from '../service/loader.service';
import { HttpClient } from '@angular/common/http';
import { NotificationService } from '../service/notification.service';
import { AuthService } from '../service/auth.service';
import { PopupService } from '../service/popup.service';
import { HardwareService } from '../service/hardware.service';
import { CashJournalService, CashOpenType, CashOpenOrigin } from '../service/cash-journal.service';
import { OrderContextService } from '../service/order-context.service';
import { BarcodeScannerService } from '../service/barcode-scanner.service';


@Component({
  selector: 'app-add-order',
  templateUrl: './add-order.component.html',
  styleUrls: ['./add-order.component.css']
})
export class AddOrderComponent implements OnInit, OnDestroy {
  newOrder: Order = new Order([], new Date(), 0);
  barcode: string = '';
  products: Product[] = [];
  productsToDisplay: Product[] = [];
  orderState!: Number;
  client?: Client;
  orderId?: number;
  url?: string;

  // Track original order state for detecting payment changes on update
  private originalOrderPaymentState?: { isPaid: boolean; advancePayment: number };
  private originalVirtualAmount: number = 0; // Tracks the amount effectively 'owned' by this order if it was already virtual

  @ViewChild('barcodeInput') barcodeInput!: ElementRef;
  @ViewChild('searchInput') searchInput!: ElementRef;

  // New properties for dropdown and loaders
  showDropdown: boolean = false;
  isLoadingProducts: boolean = false;
  searchTerm: string = '';
  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();
  private autoFocusEnabled: boolean = true;

  private readonly MIN_BARCODE_LENGTH = 3;

  // Custom tooltip properties
  tooltipVisible: boolean = false;
  tooltipText: string = '';
  tooltipX: number = 0;
  tooltipY: number = 0;

  // Virtual Order Validation
  unconfirmedF8Count: number = 0;
  availableVirtualBalance: number = 0;

  get effectiveDisplayBalance(): number {
    return this.availableVirtualBalance + (this.originalVirtualAmount || 0);
  }

  get canBeVirtual(): boolean {
    if (this.unconfirmedF8Count <= 0) return false;

    // Rule: Cannot be virtual if unpaid AND no advance payment
    if (!this.newOrder.isOrderPaid() && (this.newOrder.advancePayment || 0) <= 0) {
      return false;
    }

    // Rule 3: Check solvency
    // If update mode, we add back the original amount we are "replacing"
    const currentOrderAmount = this.getCurrentOrderAmountForValidation();
    const effectiveBalance = this.availableVirtualBalance + (this.originalVirtualAmount || 0);

    if (currentOrderAmount > effectiveBalance + 0.01) { // 0.01 tolerance
      return false;
    }

    return true;
  }

  getCurrentOrderAmountForValidation(): number {
    const isPaid = this.newOrder.isOrderPaid();
    // Strict parsing for advance payment (handle strings from input)
    const advance = this.newOrder.advancePayment ? parseFloat(this.newOrder.advancePayment.toString()) : 0;
    const total = (this.newOrder.total || 0) - (this.newOrder.discount || 0);

    // For Unpaid orders, we ONLY care about the Cash advanced (F8 equivalent)
    const amountToCheck = isPaid ? total : advance;

    return amountToCheck;
  }

  get solvencyGap(): number {
    const amount = this.getCurrentOrderAmountForValidation();
    const effectiveBalance = this.availableVirtualBalance + (this.originalVirtualAmount || 0);
    return Math.max(0, amount - effectiveBalance);
  }

  // Helper for template: get total quantity
  getTotalQuantity(): number {
    if (!this.newOrder.orderLines) return 0;
    return this.newOrder.orderLines.reduce((sum, l) => sum + ((l.quantity || 0) - (l.returnedQuantity || 0)), 0);
  }

  // Method to calculate line total
  getLineTotal(orderLine: OrderLine): number {
    const price = orderLine.realSellingPrice ?? orderLine.product?.price ?? 0;
    const quantity = (orderLine.quantity ?? 0) - (orderLine.returnedQuantity || 0);
    return Math.round((price * quantity) * 100) / 100; // Round to 2 decimal places
  }

  // Method to get formatted line total for display
  getFormattedLineTotal(orderLine: OrderLine): string {
    const total = this.getLineTotal(orderLine);
    return total.toFixed(2);
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private renderer: Renderer2,
    private productService: ProductService,
    private orderService: OrderService,
    private dialog: MatDialog,
    private notificationService: NotificationService,
    private stateService: StateService,
    private clientService: ClientService,
    private loaderService: LoaderService,
    private http: HttpClient,
    private authService: AuthService,
    private popupService: PopupService,
    private cdr: ChangeDetectorRef,
    private hardwareService: HardwareService,
    private cashJournalService: CashJournalService,
    private orderContextService: OrderContextService,
    private barcodeScannerService: BarcodeScannerService
  ) {

    // Setup debounced search
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(term => {
      this.searchProducts(term);
    });
  }


  ngOnDestroy(): void {
    if (this.router.url === '/updateOrder') {
      this.stateService.setOrderId(0);
    }
    this.renderer.removeClass(document.body, 'your-component-bg');
    this.orderContextService.setCurrentOrder(null);
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:click', ['$event'])
  handleOutsideClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    // Close dropdown when clicking outside, but not when clicking on the search input or dropdown
    if (this.barcodeInput && !this.barcodeInput.nativeElement.contains(target) &&
      !target.closest('.product-dropdown')) {
      this.searchTerm = '';
      this.showDropdown = false;
    }
  }

  // Show dropdown when search input is focused
  onSearchFocus(): void {
    this.showDropdown = true;
    // If no search term, show all products
    if (!this.searchTerm || this.searchTerm.trim() === '') {
      this.productsToDisplay = this.products.slice(0, 10);
    }
  }

  processBarcode(barcode: string): void {
    // Unfocus active input & clear stale manual input formatting buffers
    if (document.activeElement && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    this.inputValues.clear();

    // Always close dropdown and clear search field immediately so scanner input
    // never triggers the partial-search dropdown (debounce may still be pending).
    this.showDropdown = false;
    this.searchTerm = '';
    if (this.searchInput && this.searchInput.nativeElement) {
      this.searchInput.nativeElement.value = '';
    }
    // Cancel any debounced search that might have been queued by leaked scan chars
    this.searchSubject.next('');

    const cleanBarcode = barcode.trim();

    // Parse barcode - keep as string to handle large barcodes without precision loss
    const parsedBarcode = this.parseBarcode(cleanBarcode);

    console.log('DEBUG: Scanned barcode:', cleanBarcode);
    console.log('DEBUG: Parsed barcode:', parsedBarcode);
    console.log('DEBUG: Products available:', this.products.length);
    console.log('DEBUG: Product barcodes:', this.products.map(p => ({ name: p.name, code: p.codeBare, codeType: typeof p.codeBare })));

    if (parsedBarcode === null) {
      this.showDangerSnackbar('Code-barre invalide ou format non reconnu!');
      return;
    }

    // Search for product by barcode: STRICT EXACT MATCH ONLY
    const product = this.products.find(p => {
      if (!p.codeBare) return false;
      return p.codeBare.toString().trim() === parsedBarcode;
    });

    console.log('DEBUG: Found product:', product);

    if (product && product.codeBare) {
      this.addOrderLine(product);
      // Provide visual feedback for successful scan
      this.showSnackbar(`✓ ${product.name} a été ajouté à la commande`);
    } else {
      this.showDangerSnackbar(`Produit avec code-barre ${parsedBarcode} non trouvé!`);
    }
  }

  /**
   * Parse barcode string to string (to handle large barcodes without precision loss)
   * Handles various barcode formats
   */
  private parseBarcode(barcode: string): string | null {
    if (!barcode || barcode.length === 0) {
      return null;
    }

    // Map French AZERTY unshifted number row characters to digits
    const azertyMap: { [key: string]: string } = {
      '&': '1', 'é': '2', '"': '3', "'": '4', '(': '5',
      '-': '6', 'è': '7', '_': '8', 'ç': '9', 'à': '0'
    };

    let converted = '';
    for (const char of barcode) {
      converted += azertyMap[char] || char;
    }

    // Remove any non-numeric characters from the barcode
    const numericBarcode = converted.replace(/\D/g, '');

    if (numericBarcode.length === 0) {
      return null;
    }

    // Validate that barcode contains only digits and is at least 3 characters
    if (!/^\d+$/.test(numericBarcode) || numericBarcode.length < this.MIN_BARCODE_LENGTH) {
      return null;
    }

    return numericBarcode;
  }

  ngOnInit(): void {
    // Scroll to top of the page when component initializes
    window.scrollTo(0, 0);

    this.renderer.addClass(document.body, 'your-component-bg');
    this.initializeComponent();

    // Receive barcodes from the global scanner service (no field pollution, no dialog interference)
    this.barcodeScannerService.scanned$
      .pipe(takeUntil(this.destroy$))
      .subscribe(barcode => {
        if (this.dialog.openDialogs.length > 0) return;
        this.processBarcode(barcode);
      });

    // Ensure product tooltips have transparent backgrounds
    document.documentElement.style.setProperty('--mdc-plain-tooltip-container-color', 'transparent');

    // Fetch unconfirmed F8 count for validation
    this.cashJournalService.getUnconfirmedCount().subscribe(count => {
      this.unconfirmedF8Count = count;
      // If not eligible, force virtual off (but preserve in UPDATE mode)
      if (!this.canBeVirtual && this.newOrder.isVirtual && !this.orderId) {
        this.newOrder.isVirtual = false;
      }
    });

    // Fetch available virtual balance
    this.cashJournalService.getAvailableVirtualBalance().subscribe(balance => {
      this.availableVirtualBalance = balance;
      // If not eligible, force virtual off (but preserve in UPDATE mode)
      if (!this.canBeVirtual && this.newOrder.isVirtual && !this.orderId) {
        this.newOrder.isVirtual = false;
      }
    });
  }

  initializeComponent(): void {
    this.route.queryParams.subscribe((params: any) => {
      this.url = this.router.url.split('?')[0];
      const orderId = Number(params['id']);
      const clientId = Number(params['clientId']);

      if (orderId) {
        // CRITICAL: Store orderId as component property for UPDATE mode detection
        this.orderId = orderId;
        this.loaderService.show();
        this.loadOrder(orderId);
      } else if (this.url === '/addOrder' && !isNaN(clientId)) {
        // Instant Client fallback for zero-latency scan UX (ID 1 = Divers)
        if (clientId === 1) {
          this.client = { id: 1, firstName: 'Divers', lastName: '', email: '', phoneNumber: '' } as any;
          this.newOrder = new Order([], new Date(), 0);
          this.newOrder.clientDTO = this.client;
          this.orderContextService.setCurrentOrder(this.newOrder);
        }

        this.clientService.getClientById(clientId).subscribe({
          next: (client) => {
            this.client = client;
            if (!this.newOrder) {
              this.newOrder = new Order([], new Date(), 0);
            }
            this.newOrder.clientDTO = client;
            // Rendre la commande en cours accessible pour le flux F8 (avant sauvegarde)
            this.orderContextService.setCurrentOrder(this.newOrder);
          },
          error: (err) => {
            console.error('Erreur lors du chargement du client', err);
          }
        });
      }
    });

    // Load products with loading indicator
    this.isLoadingProducts = true;
    this.productService.listProducts().pipe(
      finalize(() => {
        this.isLoadingProducts = false;
        // Hide the global fullscreen loader shown during scan → /addOrder navigation
        this.loaderService.hide();
      })
    ).subscribe((data: Product[]) => {
      this.products = data;
      this.productsToDisplay = this.products.slice(0, 10);

      // Process barcode that arrived during navigation (global scan → /addOrder redirect)
      // MUST be called here (after products are loaded), NOT in finalize()
      // finalize() runs before subscribe(), so products would still be empty at that point
      const pending = this.barcodeScannerService.consumePendingBarcode();
      if (pending) this.processBarcode(pending);
    });
  }


  loadOrder(orderId: number): void {
    this.orderService.getOrderById(orderId).pipe(
      finalize(() => {
        this.loaderService.hide();
      })
    ).subscribe(order => {
      // Ensure we preserve the original realPurchasePrice and realSellingPrice values
      if (order.orderLines) {
        order.orderLines.forEach(line => {
          // Make sure these values are preserved and not overwritten
          line.realPurchasePrice = line.realPurchasePrice ?? line.product?.purchasePrice;
          line.realSellingPrice = line.realSellingPrice ?? line.product?.price;
        });
      }

      // Create new order with all properties including notes
      this.newOrder = new Order(
        order.orderLines,
        order.orderDate,
        order.discount,
        order.isPaid,
        order.clientDTO,
        order.advancePayment,
        order.id,
        order.notes, // Add notes parameter
        order.isVirtual, // Add isVirtual parameter
        order.netTotal,
        order.hasReturns,
        order.totalRefunded,
        order.totalDiscountAdjustment
      );
      this.client = this.newOrder.clientDTO;

      // Rendre la commande accessible pour le flux F8 (lien immédiat si l'utilisateur presse F8 pendant la consultation)
      this.orderContextService.setCurrentOrder(this.newOrder);

      // Save original payment state for detecting changes on update
      this.originalOrderPaymentState = {
        isPaid: order.isPaid ?? false,
        advancePayment: order.advancePayment ?? 0
      };

      // If it was already virtual, calculate its "credit" to add back to the balance
      if (this.newOrder.isVirtual) {
        // CRITICAL: Virtual Orders ALWAYS consume their TOTAL amount, regardless of payment status
        // This ensures the order can be modified even when global balance is low
        const total = (this.newOrder.total || 0) - (this.newOrder.discount || 0);
        this.originalVirtualAmount = total;
      }
    });

  }

  @HostListener('input', ['$event.target'])
  onInput(input: EventTarget | null): void {
    this.newOrder.calculateTotal();

    // Auto-disable virtual if conditions are no longer met
    // BUT: In UPDATE mode, preserve virtual status (user can manually change it if needed)
    if (!this.canBeVirtual && this.newOrder.isVirtual && !this.orderId) {
      this.newOrder.isVirtual = false;
      this.showSnackbar('Mode Virtuel désactivé (Conditions non remplies)');
    }
  }

  getProducts(): Observable<Product[]> {
    return of(this.products);
  }

  searchProducts(query: string): void {
    if (!query || query.trim() === '') {
      this.productsToDisplay = this.products.slice(0, 10);
      this.isLoadingProducts = false;
      return;
    }

    this.isLoadingProducts = true;

    this.getProducts().pipe(
      map(products => products.filter(product => {
        const normalizeString = (str: string) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        const lowerCaseQuery = normalizeString(query);
        const matchesQuery = (product.name && normalizeString(product.name).includes(lowerCaseQuery)) ||
          (product.description && normalizeString(product.description).includes(lowerCaseQuery)) ||
          (product.codeBare && product.codeBare.toString().includes(lowerCaseQuery)) ||
          lowerCaseQuery.split(' ').every(term =>
            (product.name && normalizeString(product.name).includes(term)) ||
            (product.description && normalizeString(product.description).includes(term))
          );
        return matchesQuery;
      })),
      finalize(() => {
        this.isLoadingProducts = false;
      })
    ).subscribe(products => {
      this.productsToDisplay = products.slice(0, 10);
    });
  }

  onInputChange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.searchTerm = inputElement.value;

    if (this.searchTerm.length > 0) {
      this.showDropdown = true;
      this.searchSubject.next(this.searchTerm);
    } else {
      // Still show dropdown but with default products
      this.productsToDisplay = this.products.slice(0, 10);
    }
  }

  openDeleteConfirmation(position: number): void {
    const dialogRef = this.dialog.open(DeleteConfirmationDialogComponent, {
      data: { msg: 'Voulez-vous vraiment supprimer cette Ligne de commande ?' }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.deleteOrderLine(position);
        this.showSnackbar('Ligne de commande supprimée !');
      }
    });
  }

  deleteOrderLine(position: number): void {
    const line = this.newOrder.orderLines?.[position];
    if (line && (line.returnedQuantity || 0) > 0) {
      this.showDangerSnackbar(`⚠️ Impossible de supprimer cet article car ${line.returnedQuantity} unités ont déjà été retournées via un avoir.`);
      return;
    }
    this.newOrder.orderLines?.splice(position, 1);
    this.newOrder.calculateTotal();

    // Reset discount if no order lines remain
    if (!this.newOrder.orderLines || this.newOrder.orderLines.length === 0) {
      this.newOrder.discount = 0;
    }
  }
  get isDiscountDisabled(): boolean {
    const lines = this.newOrder.orderLines ?? [];
    return lines.length === 0 || lines.every(line => line.product?.codeBare?.toString() === '-1' || line.product?.codeBare === -1);
  }


  addOrderLine(product: Product): void {
    if (!this.newOrder.orderLines) {
      this.newOrder.orderLines = [];
    }

    const cleanProductCode = product.codeBare?.toString().trim();
    const existingIndex = this.newOrder.orderLines.findIndex(ol => {
      if (!ol.product) return false;
      if (ol.product.id && product.id && ol.product.id === product.id) return true;
      const olCode = ol.product.codeBare?.toString().trim();
      return olCode && cleanProductCode && olCode === cleanProductCode;
    });

    if (existingIndex !== -1) {
      // Increment quantity
      const line = this.newOrder.orderLines[existingIndex];
      line.quantity = (Number(line.quantity) || 0) + 1;

      // Clear any cached formatting values for this line so getDisplayValue returns the updated model quantity
      if (line.product?.codeBare) {
        this.inputValues.delete(`${line.product.codeBare}_quantity`);
      }

      // Move this line to the top for quicker access
      if (existingIndex !== 0) {
        this.newOrder.orderLines.splice(existingIndex, 1);
        this.newOrder.orderLines.unshift(line);
      }
    } else {
      const newOrderLine = new OrderLine(
        product,
        1,
        product.purchasePrice ?? 0,
        product.price ?? 0
      );
      newOrderLine.realPurchasePrice = product.purchasePrice ?? 0;
      newOrderLine.realSellingPrice = product.price ?? 0;
      // Place new line at the top rather than bottom
      this.newOrder.orderLines.unshift(newOrderLine);
    }

    // Force array immutability update so Angular change detection updates input rows immediately
    this.newOrder.orderLines = [...this.newOrder.orderLines];
    this.newOrder.calculateTotal();

    // Clear search input
    this.searchTerm = '';

    if (this.searchInput && this.searchInput.nativeElement) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.blur();
    }

    this.showDropdown = false;
  }

  // Enable or disable auto-focusing
  setAutoFocus(enabled: boolean): void {
    this.autoFocusEnabled = enabled;
  }

  // Focus the search input manually when needed
  focusSearchInput(): void {
    if (this.autoFocusEnabled && this.searchInput) {
      this.searchInput.nativeElement.focus();
    }
  }


  showSnackbar(msg: string): void {
    this.notificationService.success(msg);
  }

  showDangerSnackbar(msg: string): void {
    this.popupService.showDanger(msg);
  }

  addNoPaidOrder(): Promise<boolean> {
    const dialogRef = this.dialog.open(IsNoPaidOrderPopUpComponent, {
      data: { msg: 'Êtes-vous sûr de vouloir ajouter une commande impayée ? Ce processus ne peut pas être annulé.' }
    });
    return new Promise((resolve) => {
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.addOrder().then(resolve).catch(() => resolve(false));
        } else {
          resolve(false);
        }
      });
    });
  }

  confirmModification(): Promise<boolean> {
    const dialogRef = this.dialog.open(IsNoPaidOrderPopUpComponent, {
      data: { msg: 'Êtes-vous sûr de vouloir modifier cette commande ?' }
    });
    return new Promise((resolve, reject) => {
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.updateOrder().then(resolve).catch(reject);
        } else {
          resolve(false);
        }
      });
    });
  }

  addOrderAndReset(): void {
    this.validateOrder().then((isValid) => {
      if (isValid) {
        // ✅ Removed spam notification - intelligent backend handles reconciliation notifications
        // this.showSnackbar('Commande ajoutée avec succès !');
        const orderId = this.stateService.getOrderId(); // Get the newly created order ID
        this.router.navigate(['/orderDetails'], { queryParams: { id: orderId } }); // Navigate to orderDetails page with orderId
      } else {
        // Order validation failed silently
      }
    }).catch((error) => {
      // Handle error silently
    });
  }

  updateOrderAndReset(): void {
    this.confirmModification().then((isValid) => {
      if (isValid) {
        // this.showSnackbar('Commande Modifiée avec succès !'); // ❌ Disabled - using ONLY intelligent notifications
        const orderId = this.stateService.getOrderId(); // Get the updated order ID
        this.router.navigate(['/orderDetails'], { queryParams: { id: orderId } }); // Navigate to orderDetails page with orderId
      } else {
        // Order validation failed silently
      }
    }).catch((error) => {
      // Handle error silently
    });
  }

  validateSellingPrice(orderLine: OrderLine): void {
    const purchasePrice = orderLine.realPurchasePrice ?? orderLine.product?.purchasePrice ?? 0;
    if ((orderLine.realSellingPrice ?? 0) < purchasePrice) {
      this.showDangerSnackbar(`⚠️ Le prix de vente est inférieur au prix d'achat (${purchasePrice} MAD).`);
    }
    this.newOrder.calculateTotal();
  }

  updateSellingPrice(orderLine: OrderLine, value: any): void {
    // Handle comma to dot conversion for decimal input
    let inputValue = value;
    if (typeof value === 'string') {
      // Replace comma with dot for decimal separator consistency
      inputValue = value.replace(',', '.');

      // Remove any non-numeric characters except dot and minus
      let cleanValue = inputValue.replace(/[^\d.-]/g, '');

      // Ensure only one dot
      const dotCount = (cleanValue.match(/\./g) || []).length;
      if (dotCount > 1) {
        const firstDotIndex = cleanValue.indexOf('.');
        cleanValue = cleanValue.substring(0, firstDotIndex + 1) + cleanValue.substring(firstDotIndex + 1).replace(/\./g, '');
      }

      // If ends with dot (e.g., "12."), preserve it during input
      if (cleanValue.endsWith('.') && cleanValue !== '.') {
        const numericPart = parseFloat(cleanValue);
        if (!isNaN(numericPart)) {
          orderLine.realSellingPrice = numericPart;
          this.validateSellingPrice(orderLine);
          this.onOrderLineChange();
          return;
        }
      }

      // Parse the cleaned value
      const parsedValue = parseFloat(cleanValue) || 0;
      orderLine.realSellingPrice = Math.round(parsedValue * 100) / 100; // Round to 2 decimal places

    } else if (typeof value === 'number') {
      orderLine.realSellingPrice = Math.round(value * 100) / 100;
    }

    this.validateSellingPrice(orderLine);
    this.onOrderLineChange();
  }

  onOrderLineChange(): void {
    this.newOrder.total = this.newOrder.calculateTotal();
  }

  // Method to show tooltip
  showTooltip(event: MouseEvent, description?: string): void {
    if (!description) return;

    this.tooltipText = description;
    this.tooltipX = event.clientX + 10; // Position to the right of the cursor
    this.tooltipY = event.clientY - 30; // Position above the cursor
    this.tooltipVisible = true;
  }

  // Method to hide tooltip
  hideTooltip(): void {
    this.tooltipVisible = false;
  }

  // Method to get purchase price tooltip text
  getPurchasePriceTooltip(orderLine: any): string {
    const actualPurchasePrice = orderLine.product?.purchasePrice ?? 0;
    const realPurchasePrice = orderLine.realPurchasePrice ?? actualPurchasePrice;

    let tooltipText = `Actual Purchase Price: ${actualPurchasePrice.toFixed(2)} MAD`;

    // Only show real purchase price if it's different from actual
    if (realPurchasePrice !== actualPurchasePrice) {
      tooltipText += `\nReal Purchase Price: ${realPurchasePrice.toFixed(2)} MAD`;
    }

    return tooltipText;
  }

  // Add missing business logic functions
  private adjustWidth(input: HTMLInputElement): void {
    const tempSpan = this.renderer.createElement('span');
    this.renderer.setStyle(tempSpan, 'visibility', 'hidden');
    this.renderer.setStyle(tempSpan, 'white-space', 'pre');
    this.renderer.setStyle(tempSpan, 'font', getComputedStyle(input).font);
    this.renderer.appendChild(tempSpan, this.renderer.createText(input.value || input.placeholder));
    this.renderer.appendChild(document.body, tempSpan);
    input.style.width = `${tempSpan.offsetWidth}px`;
    this.renderer.removeChild(document.body, tempSpan);
  }

  // Enhanced order validation
  validateOrder(): Promise<boolean> {
    // Check if order has lines
    if (!this.newOrder.orderLines || this.newOrder.orderLines.length === 0) {
      this.showDangerSnackbar('⚠️ Veuillez ajouter au moins un produit à la commande.');
      return Promise.resolve(false);
    }

    // Check if client is selected
    if (!this.newOrder.clientDTO) {
      this.showDangerSnackbar('⚠️ Veuillez sélectionner un client.');
      return Promise.resolve(false);
    }

    // Validate selling prices
    const invalidProducts = (this.newOrder.orderLines ?? [])
      .filter(line => {
        const purchasePrice = line.realPurchasePrice ?? line.product?.purchasePrice ?? 0;
        return (line.realSellingPrice ?? 0) < purchasePrice;
      })
      .map(line => line.product?.name || 'Produit inconnu');

    if (invalidProducts.length > 0) {
      this.dialog.open(InvalidSellingPriceDialogComponent, {
        data: { invalidProducts }
      });
      return Promise.resolve(false);
    }

    // Validate advance payment
    if (this.newOrder.isPaid && this.newOrder.advancePayment && this.newOrder.advancePayment > 0) {
      this.showDangerSnackbar('⚠️ Le paiement d\'avance ne peut pas être défini pour une commande payée.');
      return Promise.resolve(false);
    }

    // Validate total amount
    const calculatedTotal = this.newOrder.calculateTotal();
    if (calculatedTotal <= 0) {
      this.showDangerSnackbar('⚠️ Le montant total de la commande doit être supérieur à 0.');
      return Promise.resolve(false);
    }

    if (this.newOrder.isPaid) {
      this.newOrder.advancePayment = 0;
      return this.addOrder();
    } else {
      return this.addNoPaidOrder();
    }
  }

  // Enhanced addOrder method with better error handling
  addOrder(): Promise<boolean> {
    // Ensure all required fields are set
    this.newOrder.total = this.newOrder.calculateTotal();
    this.newOrder.clientDTO = this.client;

    // Ensure order lines have proper values
    if (this.newOrder.orderLines) {
      this.newOrder.orderLines.forEach(line => {
        line.realPurchasePrice = line.realPurchasePrice ?? line.product?.purchasePrice ?? 0;
        line.realSellingPrice = line.realSellingPrice ?? line.product?.price ?? 0;
        if (!line.quantity || line.quantity <= 0) {
          line.quantity = 1;
        }
      });
    }

    // Use only the global loader
    this.loaderService.show();

    return new Promise((resolve, reject) => {
      this.orderService.addOrder(this.newOrder).subscribe(
        (response: HttpResponse<Order>) => {
          this.loaderService.hide();

          this.orderState = response.status;
          const createdOrder = response.body;
          this.stateService.setOrderId(createdOrder?.id ?? 0);

          // Rendre la commande accessible pour le flux F8 (lien immédiat si besoin)
          if (createdOrder) {
            this.orderContextService.setCurrentOrder(createdOrder as any);
          }

          // Open cash drawer if there's cash payment AND it's NOT a virtual order
          // Virtual orders (F8 rush mode) already had cash collected via F8
          const hasCashPayment = this.newOrder.isPaid || (this.newOrder.advancePayment && this.newOrder.advancePayment > 0);
          const isVirtualOrder = this.newOrder.isVirtual === true;

          if (hasCashPayment && createdOrder && !isVirtualOrder) {
            this.openCashDrawerForOrder(createdOrder);
          } else if (isVirtualOrder) {
            console.log('💡 Virtual order - skipping cash drawer (money already collected via F8)');
          }

          resolve(this.orderState === 200);
        },
        (error) => {
          this.loaderService.hide();

          const errorMsg = error?.error?.message || '';

          if (errorMsg.includes('Stock insuffisant')) {
            const productNameMatch = errorMsg.match(/produit\s*:\s*(.+)/);
            const productIdMatch = errorMsg.match(/ID du produit\s*:\s*(\d+)/);
            const stockMatch = errorMsg.match(/Stock disponible\s*:\s*([\d.]+)/);

            const productName = productNameMatch ? productNameMatch[1].trim() : '';
            const productId = productIdMatch ? parseInt(productIdMatch[1], 10) : 0;
            const available = stockMatch ? parseFloat(stockMatch[1]) : 0;

            // Open dialog to get new quantity
            const dialogRef = this.dialog.open(InsufficientQuantityDialogComponent, {
              data: {
                productName,
                available,
                newQuantity: available
              },
              disableClose: false,
              width: '450px',
              panelClass: 'transparent-dialog-container'
            });

            dialogRef.afterClosed().subscribe(newQty => {
              if (newQty !== null && newQty !== undefined) {
                // Update product quantity using the product service
                this.productService.updateProductQuantity(productId, parseFloat(newQty)).subscribe({
                  next: () => {
                    // Try adding the order again
                    this.addOrder().then(success => {
                      if (success) {
                        resolve(true);
                      } else {
                        resolve(false);
                      }
                    }).catch(err => {
                      reject(err);
                    });
                  },
                  error: err => {
                    this.dialog.open(GenericErrorDialogComponent, {
                      data: { message: 'Erreur lors de la mise à jour de la quantité du produit.' }
                    });
                    resolve(false);
                  }
                });
              } else {
                console.log('Dialog cancelled by user');
                resolve(false);
              }
            });
          } else {
            // Show generic error dialog
            this.dialog.open(GenericErrorDialogComponent, {
              data: { message: errorMsg || 'Une erreur est survenue lors de la création de la commande.' }
            });
            resolve(false);
          }
        }
      );
    });
  }

  // Enhanced updateOrder method
  updateOrder(): Promise<boolean> {
    // Ensure all order lines have their realPurchasePrice and realSellingPrice set correctly
    if (this.newOrder.orderLines) {
      this.newOrder.orderLines.forEach(line => {
        // Make sure these values are preserved before sending the update
        line.realPurchasePrice = line.realPurchasePrice ?? line.product?.purchasePrice ?? 0;
        line.realSellingPrice = line.realSellingPrice ?? line.product?.price ?? 0;
        if (!line.quantity || line.quantity <= 0) {
          line.quantity = 1;
        }
      });
    }

    this.newOrder.total = this.newOrder.calculateTotal();
    this.newOrder.clientDTO = this.client;

    // Use only the global loader
    this.loaderService.show();

    return new Promise((resolve, reject) => {
      this.orderService.updateOrder(this.newOrder).subscribe(
        (response: HttpResponse<Order>) => {
          this.loaderService.hide();

          this.orderState = response.status;
          const updatedOrder = response.body;
          this.stateService.setOrderId(updatedOrder?.id ?? 0);

          // Check if there's additional payment on update AND it's not a virtual order
          if (updatedOrder && this.originalOrderPaymentState) {
            const hasAdditionalPayment = this.detectAdditionalPayment(updatedOrder);
            const isVirtualOrder = this.newOrder.isVirtual === true;

            if (hasAdditionalPayment && !isVirtualOrder) {
              this.openCashDrawerForOrderUpdate(updatedOrder);
            } else if (isVirtualOrder) {
              console.log('💡 Virtual order update - skipping cash drawer');
            }
          }

          resolve(this.orderState === 200);

        },
        (error) => {
          this.loaderService.hide();

          const errorMsg = error?.error?.message || '';

          if (errorMsg.includes('Stock insuffisant')) {
            const productNameMatch = errorMsg.match(/produit\s*:\s*(.+?)\n/);
            const productIdMatch = errorMsg.match(/ID du produit\s*:\s*(\d+)/);
            const stockMatch = errorMsg.match(/Stock disponible\s*:\s*([\d.]+)/);

            const productName = productNameMatch ? productNameMatch[1].trim() : '';
            const productId = productIdMatch ? parseInt(productIdMatch[1], 10) : 0;
            const available = stockMatch ? parseFloat(stockMatch[1]) : 0;

            // Open dialog to get new quantity
            const dialogRef = this.dialog.open(InsufficientQuantityDialogComponent, {
              data: {
                productName,
                available,
                newQuantity: available
              },
              disableClose: false,
              width: '450px',
              panelClass: 'transparent-dialog-container'
            });

            dialogRef.afterClosed().subscribe(newQty => {
              if (newQty !== null && newQty !== undefined) {
                // Update product quantity using the product service
                this.productService.updateProductQuantity(productId, parseFloat(newQty)).subscribe({
                  next: () => {
                    console.log(`Product ${productId} quantity updated to ${newQty}`);

                    // Try updating the order again
                    this.updateOrder().then(success => {
                      if (success) {
                        console.log('Order updated successfully after quantity update');
                        resolve(true);
                      } else {
                        console.error('Failed to update order after quantity update');
                        resolve(false);
                      }
                    }).catch(err => {
                      console.error('Error updating order after quantity update:', err);
                      reject(err);
                    });
                  },
                  error: err => {
                    console.error('Error updating product quantity:', err);
                    this.dialog.open(GenericErrorDialogComponent, {
                      data: { message: 'Erreur lors de la mise à jour de la quantité du produit.' }
                    });
                    resolve(false);
                  }
                });
              } else {
                console.log('Dialog cancelled by user');
                resolve(false);
              }
            });
          } else {
            this.dialog.open(GenericErrorDialogComponent, {
              data: { message: errorMsg || 'Une erreur est survenue lors de la mise à jour de la commande.' }
            });
            resolve(false);
          }
        }
      );
    });
  }

  // Method to adjust quantity by fixed step of 0.001
  adjustQuantity(orderLine: OrderLine, direction: number): void {
    const netQuantity = (orderLine.quantity || 0) - (orderLine.returnedQuantity || 0);
    const step = 0.001; // Increment/decrement by 0.001
    const returnedQty = orderLine.returnedQuantity || 0;
    
    // We increment/decrement the NET quantity
    let newNetQuantity = netQuantity + (direction * step);

    // SECURITY: Block quantity reduction for non-admin users only
    if (direction < 0 && newNetQuantity < netQuantity) {
      const isAdmin = this.checkIfUserIsAdmin();
      if (!isAdmin) {
        this.showDangerSnackbar('⚠️ Seuls les administrateurs peuvent réduire les quantités de produits.');
        return;
      }
      
      // HARD LIMIT: Net quantity cannot go below 0
      if (newNetQuantity < 0) {
        this.showDangerSnackbar(`⚠️ Impossible de descendre en dessous de zéro.`);
        newNetQuantity = 0;
      }
    }

    // Ensure net quantity is never negative
    if (newNetQuantity < 0) {
      newNetQuantity = 0;
    }

    // Update the internal INITIAL quantity: Initial = Net + Returned
    orderLine.quantity = Math.round((newNetQuantity + returnedQty) * 1000) / 1000;
    this.onOrderLineChange();
  }

  // Method to update quantity from input
  updateQuantity(orderLine: OrderLine, value: any): void {
    // Handle different input types
    let inputValue = value;

    if (typeof value === 'string') {
      // Replace comma with dot for decimal separator consistency
      inputValue = value.replace(',', '.');

      // Handle special cases for decimal input
      if (inputValue === '' || inputValue === '.') {
        orderLine.quantity = 0;
        this.onOrderLineChange();
        return;
      }

      // Remove any non-numeric characters except dot and minus
      let cleanValue = inputValue.replace(/[^\d.-]/g, '');

      // Ensure only one dot and handle negative numbers
      const dotCount = (cleanValue.match(/\./g) || []).length;
      if (dotCount > 1) {
        const firstDotIndex = cleanValue.indexOf('.');
        cleanValue = cleanValue.substring(0, firstDotIndex + 1) + cleanValue.substring(firstDotIndex + 1).replace(/\./g, '');
      }

      // If ends with dot (e.g., "12."), preserve it by storing as string temporarily
      if (cleanValue.endsWith('.') && cleanValue !== '.') {
        const numericPart = parseFloat(cleanValue);
        if (!isNaN(numericPart)) {
          // Store the numeric value but preserve input format for display
          orderLine.quantity = numericPart;
          this.onOrderLineChange();
          return;
        }
      }

      // Parse the cleaned value
      let newQuantity = parseFloat(cleanValue) || 0;

      // Allow 0 as valid quantity
      if (newQuantity < 0) {
        newQuantity = 0;
      }

      // SECURITY & AVOIR LIMITS
      const currentNetQuantity = (orderLine.quantity || 0) - (orderLine.returnedQuantity || 0);
      const returnedQty = orderLine.returnedQuantity || 0;

      if (newQuantity < currentNetQuantity) {
        const isAdmin = this.checkIfUserIsAdmin();
        if (!isAdmin) {
          // BLOCK quantity reduction for non-admin users
          this.showDangerSnackbar('⚠️ Seuls les administrateurs peuvent réduire les quantités de produits.');
          // Keep current quantity
          this.onOrderLineChange();
          return;
        }
        
        // HARD LIMIT
        if (newQuantity < 0) {
          this.showDangerSnackbar(`⚠️ La quantité nette ne peut pas être négative.`);
          newQuantity = 0;
        }
      }

      // Update Initial = Net + Returned
      orderLine.quantity = Math.round((newQuantity + returnedQty) * 1000) / 1000;

    } else if (typeof value === 'number') {
      orderLine.quantity = Math.round(value * 1000) / 1000;
    }

    this.onOrderLineChange();
  }

  // Enhanced method to handle decimal quantity input with better validation
  updateQuantityEnhanced(orderLine: OrderLine, value: any): void {
    let cleanValue = value;

    if (typeof value === 'string') {
      // Handle both French decimal separator (comma) and dot
      cleanValue = value.replace('.', ',').replace(',', '.');

      // Allow only digits and one decimal point
      cleanValue = cleanValue.replace(/[^\d.]/g, '');

      // Ensure only one decimal point
      const parts = cleanValue.split('.');
      if (parts.length > 2) {
        cleanValue = parts[0] + '.' + parts.slice(1).join('');
      }

      // Handle empty string or just a dot
      if (cleanValue === '' || cleanValue === '.') {
        orderLine.quantity = 0;
        this.onOrderLineChange();
        return;
      }

      let newQuantity = parseFloat(cleanValue);

      // Check if parsing was successful
      if (isNaN(newQuantity)) {
        newQuantity = 0;
      }

      // SECURITY & AVOIR LIMITS
      const currentNetQuantity = (orderLine.quantity || 0) - (orderLine.returnedQuantity || 0);
      const returnedQty = orderLine.returnedQuantity || 0;

      if (newQuantity < currentNetQuantity) {
        const isAdmin = this.checkIfUserIsAdmin();
        if (!isAdmin) {
          // BLOCK quantity reduction for non-admin users
          this.showDangerSnackbar('⚠️ Seuls les administrateurs peuvent réduire les quantités de produits.');
          this.onOrderLineChange();
          return;
        }

        // HARD LIMIT
        if (newQuantity < 0) {
          this.showDangerSnackbar(`⚠️ La quantité nette ne peut pas être négative.`);
          newQuantity = 0;
        }
      }

      // Update Initial = Net + Returned
      orderLine.quantity = Math.round((newQuantity + returnedQty) * 1000) / 1000;

    } else if (typeof value === 'number') {
      orderLine.quantity = Math.round(value * 1000) / 1000;
    }

    this.onOrderLineChange();
  }

  // Method to check if current user is admin
  private checkIfUserIsAdmin(): boolean {
    // Get user role from auth service (same as nav-bar component)
    const user = this.authService.getUser();
    return user?.role === 'ADMIN';
  }

  // Method to handle textarea keydown for line breaks
  onTextareaKeydown(event: KeyboardEvent): void {
    // Always allow Enter for line breaks in textarea
    if (event.key === 'Enter') {
      // Allow normal Enter behavior for line breaks
      // Don't prevent default - let the textarea handle it naturally
      return;
    }

    // Handle Tab key for indentation
    if (event.key === 'Tab') {
      event.preventDefault();
      // Insert tab character or handle tab behavior
      const textarea = event.target as HTMLTextAreaElement;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const value = textarea.value;

      textarea.value = value.substring(0, start) + '\t' + value.substring(end);
      textarea.selectionStart = textarea.selectionEnd = start + 1;

      // Trigger change detection
      this.cdr.detectChanges();
    }
  }

  // Helper methods for decimal input handling
  private inputValues: Map<string, string> = new Map();

  getDisplayValue(orderLine: OrderLine, field: 'quantity' | 'realSellingPrice'): string {
    const key = `${orderLine.product?.codeBare}_${field}`;
    const storedValue = this.inputValues.get(key);

    if (storedValue !== undefined) {
      return storedValue;
    }

    const actualValue = field === 'quantity' 
      ? (orderLine.quantity ?? 0) - (orderLine.returnedQuantity || 0) 
      : orderLine.realSellingPrice ?? orderLine.product?.price;
    
    return actualValue?.toString() ?? '';
  }

  onDecimalInputChange(orderLine: OrderLine, field: 'quantity' | 'realSellingPrice', event: any): void {
    const key = `${orderLine.product?.codeBare}_${field}`;
    const value = event.target.value;

    // Store the raw input value to preserve formatting
    this.inputValues.set(key, value);

    // Update the model
    if (field === 'quantity') {
      this.updateQuantity(orderLine, value);
    } else {
      this.updateSellingPrice(orderLine, value);
    }
  }

  onInputBlur(orderLine: OrderLine, field: 'quantity' | 'realSellingPrice'): void {
    const key = `${orderLine.product?.codeBare}_${field}`;
    // Clear stored input value on blur to show formatted value
    this.inputValues.delete(key);
  }

  // Method to open cash drawer after order creation with payment
  private async openCashDrawerForOrder(order: Order): Promise<void> {
    try {
      // Check if hardware is available
      const isAvailable = await this.hardwareService.isDrawerAvailable();
      if (!isAvailable) {
        console.log('💰 Cash drawer not available, skipping auto-open');
        return;
      }

      // Open the drawer
      const success = await this.hardwareService.openDrawer(CashOpenOrigin.ORDER_VALIDATION);
      if (!success) {
        console.warn('⚠️ Failed to open cash drawer for order');
        return;
      }

      // Log to cash journal
      const user = this.authService.getUser();
      const username = user ? user.username : 'UNKNOWN';

      const cashAmount = order.isPaid ? order.total : (order.advancePayment ?? 0);
      const paymentType = order.isPaid ? 'Paiement complet' : `Avance: ${cashAmount} MAD`;

      const journal = {
        type: CashOpenType.WITH_ORDER,
        origin: CashOpenOrigin.ORDER_CREATION,
        username: username,
        reason: `Commande #${order.id} - ${paymentType}`,
        orderId: order.id,
        amount: cashAmount
      };

      this.cashJournalService.logEvent(journal).subscribe({
        next: () => {
          console.log(`✅ Cash drawer opened and logged for order #${order.id}`);
        },
        error: (err) => console.error('Failed to log cash open event', err)
      });
    } catch (error) {
      console.error('Error opening cash drawer for order:', error);
    }
  }

  // Detect if there's any payment change on order update (increase or decrease)
  private detectAdditionalPayment(updatedOrder: Order): boolean {
    if (!this.originalOrderPaymentState) return false;

    const original = this.originalOrderPaymentState;

    // Case 1: Payment status changed
    if (original.isPaid !== updatedOrder.isPaid) {
      return true;
    }

    // Case 2: Advance payment changed (increased or decreased)
    const newAdvance = updatedOrder.advancePayment ?? 0;
    if (newAdvance !== original.advancePayment) {
      return true;
    }

    return false;
  }


  // Open cash drawer for order update with additional payment
  private async openCashDrawerForOrderUpdate(order: Order): Promise<void> {
    try {
      const isAvailable = await this.hardwareService.isDrawerAvailable();
      if (!isAvailable) {
        console.log('💰 Cash drawer not available, skipping auto-open');
        return;
      }

      const success = await this.hardwareService.openDrawer(CashOpenOrigin.ORDER_VALIDATION);
      if (!success) {
        console.warn('⚠️ Failed to open cash drawer for order update');
        return;
      }

      // Calculate payment change amount
      const user = this.authService.getUser();
      const username = user ? user.username : 'UNKNOWN';

      let paymentChange = 0;
      let paymentType = '';

      if (!this.originalOrderPaymentState!.isPaid && order.isPaid) {
        // Order marked as paid
        const previousAdvance = this.originalOrderPaymentState!.advancePayment;
        paymentChange = (order.total ?? 0) - previousAdvance;
        paymentType = paymentChange >= 0
          ? `Paiement final: +${paymentChange.toFixed(2)} MAD`
          : `Remboursement: ${paymentChange.toFixed(2)} MAD`;
      } else if (this.originalOrderPaymentState!.isPaid && !order.isPaid) {
        // Order changed from paid to unpaid (rare case)
        paymentChange = -(this.originalOrderPaymentState!.advancePayment);
        paymentType = `Annulation paiement: ${paymentChange.toFixed(2)} MAD`;
      } else {
        // Advance payment changed
        paymentChange = (order.advancePayment ?? 0) - this.originalOrderPaymentState!.advancePayment;
        paymentType = paymentChange >= 0
          ? `Avance supplémentaire: +${paymentChange.toFixed(2)} MAD`
          : `Remboursement partiel: ${paymentChange.toFixed(2)} MAD`;
      }

      const journal = {
        type: CashOpenType.WITH_ORDER,
        origin: CashOpenOrigin.ORDER_VALIDATION,
        username: username,
        reason: `Modification Commande #${order.id} - ${paymentType}`,
        orderId: order.id,
        amount: paymentChange
      };

      this.cashJournalService.logEvent(journal).subscribe({
        next: () => {
          console.log(`✅ Cash drawer opened for order update #${order.id}`);
        },
        error: (err) => console.error('Failed to log cash open event', err)
      });
    } catch (error) {
      console.error('Error opening cash drawer for order update:', error);
    }
  }

  // Avoir Summary Helpers
  getAvoirTotal(): number {
    if (!this.newOrder.orderLines) return 0;
    return this.newOrder.orderLines.reduce((sum, line) => {
      const price = line.realSellingPrice ?? line.product?.price ?? 0;
      return sum + (price * (line.returnedQuantity || 0));
    }, 0);
  }

  getInitialGrossTotal(): number {
    if (!this.newOrder.orderLines) return 0;
    return this.newOrder.orderLines.reduce((sum, line) => {
      const price = line.realSellingPrice ?? line.product?.price ?? 0;
      return sum + (price * (line.quantity || 0));
    }, 0);
  }

  getInitialDiscount(): number {
    return (this.newOrder.discount || 0) + (this.newOrder.totalDiscountAdjustment || 0);
  }

  getNetTotal(): number {
    // Total is already calculated as net (Initial - Returned) in Order.calculateTotal()
    return (this.newOrder.total || 0) - (this.newOrder.discount || 0);
  }

}
